"use client";

import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Children, cloneElement, isValidElement } from "react";
import { useTestScope } from "./use-test-scope.ts";

type TestGateProps = {
  aktion: string;
  children: ReactNode;
};

/**
 * Sperrt eine Aktion optisch, wenn sie nicht in der Allowlist des aktiven Tests liegt.
 * Ohne aktiven Scope: unverändertes Kind.
 */
export function TestGate({ aktion, children }: TestGateProps) {
  const scope = useTestScope();
  const gesperrt =
    scope.aktiv && !scope.beobachtungsmodus && !scope.aktionErlaubt(aktion);

  if (!gesperrt) {
    return <>{children}</>;
  }

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return (
      <span
        style={{ opacity: 0.4, pointerEvents: "none" }}
        aria-disabled="true"
        title="Nicht Teil dieses Tests"
        data-test-aktion={aktion}
      >
        {children}
      </span>
    );
  }

  const el = child as ReactElement<{
    className?: string;
    style?: CSSProperties;
    title?: string;
    "aria-disabled"?: boolean | "true" | "false";
    "data-test-aktion"?: string;
  }>;

  return cloneElement(el, {
    style: {
      ...(el.props.style ?? {}),
      opacity: 0.4,
      pointerEvents: "none",
    },
    "aria-disabled": true,
    title: "Nicht Teil dieses Tests",
    "data-test-aktion": aktion,
  });
}
